import { useMemo } from 'react'

import {
  AssignedUserSelector,
  Box,
  Checkbox,
  DashboardModule,
  Link,
  StatusBadge,
  Table,
  Text,
} from '@domain-group/suit-theme'
import { digObject } from '@domain-group/javascript-utils'
import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import type { EntitiesState } from '@redux/entities'
import type { useTaskListItemsPayload } from '@hooks/useTaskListItems'
import type { TaskListItemModel } from '@models/taskListItem'
import { UseToggleListItemPayload } from '@hooks/useToggleListItem'

import { getColor } from '@functions/status'

import useTaskListItem, { generateUrls, useRelations } from '@hooks/useTaskListItem'

import DateBadge from '@components/DateBadge'
import EntitySelector from '@components/EntitySelector'

const watchEntityKeys = ['statuses', 'taskListItems']

const Title = (props: { taskListItem: TaskListItemModel }) => {
  const { taskListItem } = props

  const { parentTask } = useRelations(taskListItem)
  const { editTaskListItemUrl } = generateUrls(parentTask, { includeRedirect: true })

  return (
    <Link
      href={editTaskListItemUrl}
      textProps={{
        color: 'bodyFontColor', display: 'inline-block', variant: 'ellipsis', width: 320,
      }}
    >
      {taskListItem?.title}
    </Link>
  )
}

const Assignee = (props: { assigneeId: number, entities: EntitiesState, taskListItem: TaskListItemModel }) => {
  const {
    assigneeId, entities, entities: { images, users }, taskListItem,
  } = props

  const assignee = users[assigneeId] || {}
  // @ts-expect-error TS2538
  const image = images[assignee.image] || {}

  const taskListItemPayload = useTaskListItem(taskListItem)
  const { callbacks: { updateTaskListItem } } = taskListItemPayload

  return (
    <AssignedUserSelector
      // @ts-expect-error TS2339
      callbacks={{ selectUser: user => updateTaskListItem({ id: taskListItem.id, assignee_id: user.id }) }}
      entities={entities}
      entitySelectorComponent={EntitySelector}
      userImageKey="image"
      selectedUser={{
        id: assignee.id,
        // @ts-expect-error TS2322
        fullName: assignee.full_name,
        image: {
          url: image.thumb_url,
        },
      }}
    />
  )
}

const buildColumns = (
  entities: EntitiesState,
  selectedTaskListItemIds: number[],
  toggleItem: UseToggleListItemPayload['callbacks']['toggleItem'],
) => {
  const { brands, taskLists, taskListItems } = entities

  return [
    {
      title: '',
      dataKey: 'id',
      cellProps: {
        verticalAlign: 'middle',
        width: 18,
      },
      render: (id: number) => (
        <Checkbox
          boxProps={{ width: 18 }}
          checked={selectedTaskListItemIds.includes(id)}
          onClick={() => toggleItem(id)}
          marginRight="0"
        />
      ),
    },
    {
      title: 'Title',
      dataKey: 'title',
      cellProps: {
        verticalAlign: 'middle',
        width: 320,
      },
      // @ts-expect-error TS7006
      render: (_, item: TaskListItemModel) => (
        <Title taskListItem={item} />
      ),
    },
    {
      title: 'Brand',
      dataKey: 'brand_id',
      cellProps: {
        verticalAlign: 'middle',
        width: 320,

      },
      render: (brand_id: number) => {
        const brand = brands[brand_id] || {}

        return (
          <Text
            color="bodyFontColor"
            variant="ellipsis"
            width={320}
          >
            {brand.title || 'N/A'}
          </Text>
        )
      },
    },
    {
      title: 'Parent Task',
      dataKey: 'parent_task_id',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (parent_task_id: number) => {
        const parentTask = taskListItems[parent_task_id] || {}

        return (
          <Text
            color="bodyFontColor"
            display="inline-block"
            variant="ellipsis"
            maxWidth={240}
            style={{ whiteSpace: 'nowrap' }}
          >
            {parentTask?.title || 'Not Loaded...'}
          </Text>
        )
      },
    },
    {
      title: 'Project Title',
      dataKey: 'task_list_id',
      cellProps: {
        verticalAlign: 'middle',
      },
      render: (task_list_id: number) => {
        const taskList = taskLists[task_list_id] || {}
        // @ts-expect-error TS2339
        const { editTaskListUrl } = generateUrls(taskList)

        return (
          <Link
            href={editTaskListUrl}
            textProps={{
              color: 'bodyFontColor',
              display: 'inline-block',
              variant: 'ellipsis',
              width: 240,
            }}
          >
            {taskList?.title}
          </Link>
        )
      },
    },
    {
      title: 'Due Date',
      cellProps: {
        key: 'due_date',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'parent_task_id',
      render: (parent_task_id: number) => {
        const parentTask = taskListItems[parent_task_id] || {}
        const dueDate = digObject(parentTask, 'dates.due.date')

        if (!dueDate) return (<Text fontStyle="italic">Not Set</Text>)

        return (
          <DateBadge date={dueDate} />
        )
      },
    },
    {
      title: 'Priority',
      cellProps: {
        key: 'priority',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
      dataKey: 'parent_task_id',
      render: (parent_task_id: number) => {
        const parentTask = taskListItems[parent_task_id] || {}
        const priorityKey = digObject(parentTask, 'priority', 'default')

        return <StatusBadge color={getColor('priority', priorityKey)} ghost text={priorityKey} />
      },
    },
    {
      title: 'Assignee',
      cellProps: {
        textAlign: 'center',
        verticalAlign: 'middle',
        width: 91,
      },
      dataKey: 'assignee_id',
      render: (assigneeId: number, taskListItem: TaskListItemModel) => (
        <Assignee assigneeId={assigneeId} entities={entities} taskListItem={taskListItem} />
      ),
    },
  ]
}

interface SubTasksTableProps {
  entities: EntitiesState,
  taskListItemsPayload: useTaskListItemsPayload,
  useToggleListItemPayload: UseToggleListItemPayload,
}

const SubTasksTable = (props: SubTasksTableProps) => {
  const {
    entities,
    taskListItemsPayload: {
      callbacks: {
        loadMore,
      },
      canLoadMore,
      filteredTaskListItems,
      filteredTaskListItemsCount,
      loading,
    },
    useToggleListItemPayload,
  } = props

  const {
    updatedEntities: {
      statuses: statusesUpdatedAt,
      taskListItems: taskListItemsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { items: selectedTaskListItemIds, callbacks: { toggleItem } } = useToggleListItemPayload

  const memoColumns = useMemo(
    () => buildColumns(entities, selectedTaskListItemIds, toggleItem),
    [filteredTaskListItemsCount, statusesUpdatedAt, taskListItemsUpdatedAt, selectedTaskListItemIds.length],
  )

  return (
    <DashboardModule
      loading={loading}
      title="Tasks"
    >
      <Box flexDirection="column">
        <Table
          boxProps={{ border: 'none', overflowY: 'auto' }}
          // @ts-expect-error TS2322
          columns={memoColumns}
          // @ts-expect-error TS2322
          data={filteredTaskListItems}
          stickyHeader
        />

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredTaskListItemsCount}
          loading={loading}
        />
      </Box>
    </DashboardModule>
  )
}

export default SubTasksTable
